if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.initVariantFilter();
      }

      initVariantFilter() {
        // Build keywords from our own data attributes (set by Liquid server-side)
        const keywords = [];
        const sku = (this.dataset.initialSku || '').trim().toLowerCase();
        const option1 = (this.dataset.initialOption1 || '').trim().toLowerCase();
        const title = (this.dataset.initialTitle || '').trim().toLowerCase();

        if (sku) keywords.push(sku);
        if (option1 && option1 !== 'default title') keywords.push(option1);
        if (title && title !== 'default title' && title !== sku && title !== option1) keywords.push(title);

        if (keywords.length === 0) return;

        // Also try from VariantSelects as a secondary source
        const variantSelect = document.querySelector('variant-selects, variant-radios');
        if (variantSelect) {
          try {
            if (typeof variantSelect.updateOptions === 'function') variantSelect.updateOptions();
            if (typeof variantSelect.updateMasterId === 'function') variantSelect.updateMasterId();
            if (variantSelect.currentVariant && variantSelect.currentVariant.sku) {
              const vSku = variantSelect.currentVariant.sku.trim().toLowerCase();
              if (vSku && !keywords.includes(vSku)) keywords.push(vSku);
            }
          } catch(e) { /* ignore */ }
        }

        this._filterByKeywords(keywords);

        // After SliderComponent re-initializes (ResizeObserver), re-filter
        requestAnimationFrame(() => {
          this._filterByKeywords(keywords);
          requestAnimationFrame(() => {
            this._filterByKeywords(keywords);
          });
        });
      }

      _filterByKeywords(keywords) {
        if (!keywords || keywords.length === 0) return;

        // Must target only the <li> slides. The inner <deferred-media> and
        // modal-opener wrappers also carry data-media-id, so a loose
        // [data-media-id] selector hides the video player *inside* a slide that
        // is supposed to be visible — the slide then collapses to 1px tall and
        // the main image area looks blank.
        const viewerItems = this.querySelectorAll('[id^="GalleryViewer"] li[id^="Slide-"]');
        const thumbnailItems = this.querySelectorAll('[id^="GalleryThumbnails"] li[data-target]');

        if (viewerItems.length === 0) return;

        const checkMatch = (item) => {
          const alt = (item.dataset.mediaAlt || '').toLowerCase();
          const img = item.querySelector('img');
          const src = img ? (img.getAttribute('src') || img.src || '').toLowerCase() : '';
          const srcset = img ? (img.getAttribute('srcset') || '').toLowerCase() : '';
          return keywords.some((kw) => (alt && alt.includes(kw)) || (src && src.includes(kw)) || (srcset && srcset.includes(kw)));
        };

        let matchCount = 0;
        viewerItems.forEach((item) => {
          if (checkMatch(item)) matchCount++;
        });

        if (matchCount === 0) return; // no matches = show all (don't hide anything)

        let firstMatchingMediaId = null;

        viewerItems.forEach((item) => {
          if (checkMatch(item)) {
            item.style.display = '';
            item.classList.remove('hidden-by-variant');
            if (!firstMatchingMediaId) {
              firstMatchingMediaId = item.dataset.mediaId;
            }
          } else {
            item.style.display = 'none';
            item.classList.add('hidden-by-variant');
            item.classList.remove('is-active');
          }
        });

        if (thumbnailItems) {
          thumbnailItems.forEach((item) => {
            if (checkMatch(item)) {
              item.style.display = '';
              item.classList.remove('hidden-by-variant');
            } else {
              item.style.display = 'none';
              item.classList.add('hidden-by-variant');
            }
          });
        }

        // Reset scroll and recalculate slider pages
        const sliders = this.querySelectorAll('slider-component');
        sliders.forEach((sliderComp) => {
          if (sliderComp.slider) sliderComp.slider.scrollLeft = 0;
          if (typeof sliderComp.resetPages === 'function') {
            sliderComp.resetPages();
          }
        });

        if (firstMatchingMediaId) {
          this.setActiveMedia(firstMatchingMediaId, false);
        }

        // Custom button state logic removed; SliderComponent.update() handles it accurately.
      }

      filterMediaByVariant(variant) {
        if (!variant) return;

        const keywords = [];
        if (variant.sku && variant.sku.trim()) {
          keywords.push(variant.sku.trim().toLowerCase());
        }
        if (variant.options && Array.isArray(variant.options)) {
          variant.options.forEach((opt) => {
            if (opt && opt.trim() && opt.trim().toLowerCase() !== 'default title') {
              keywords.push(opt.trim().toLowerCase());
            }
          });
        }
        if (variant.title && variant.title.trim() && variant.title.trim().toLowerCase() !== 'default title') {
          const t = variant.title.trim().toLowerCase();
          if (!keywords.includes(t)) keywords.push(t);
        }

        if (keywords.length === 0) {
          // No keywords = show all media
          this.querySelectorAll('[id^="GalleryViewer"] li[id^="Slide-"]').forEach((item) => {
            item.style.display = '';
            item.classList.remove('hidden-by-variant');
          });
          this.querySelectorAll('[id^="GalleryThumbnails"] li[data-target]').forEach((item) => {
            item.style.display = '';
            item.classList.remove('hidden-by-variant');
          });
          const sliders = this.querySelectorAll('slider-component');
          sliders.forEach((sliderComp) => {
            if (sliderComp.slider) sliderComp.slider.scrollLeft = 0;
            if (typeof sliderComp.resetPages === 'function') sliderComp.resetPages();
          });
          return;
        }

        this._filterByKeywords(keywords);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
            if (typeof this.elements.thumbnails.resetPages === 'function') {
              this.elements.thumbnails.resetPages();
            }
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        } else {
          // Manually trigger update on the viewer slider since scroll event
          // won't fire on desktop where active item has offsetLeft = 0
          if (this.elements.viewer && typeof this.elements.viewer.update === 'function') {
            this.elements.viewer.update();
          }
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
           
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
